import {
  Box,
  Stack,
  Typography,
  Avatar,
  Badge,
  Button,
  IconButton,
  BoxProps,
  CircularProgress
} from '@mui/material';
import { getSocialMediaColor } from 'src/utils/getSocialMediaColor';
import SocialMediaIcon from 'src/components/SocialMediaIcon';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { INormalizedStream } from 'src/models/newsLetter';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  reducerRemoveTranslatedStreams,
  reducerUpdateActiveSection,
  reducerUpdateDataSelectDataSource,
  reducerUpdateMediaType,
  reducerUpdateTranslatedStreams
} from 'src/features/customNewsLetters';
import { TSocialMedia } from 'src/models/general';
import PlaylistAddCircleIcon from '@mui/icons-material/PlaylistAddCircle';
import TranslateButton from 'src/components/TranslateButton';
import {
  resetTranslation,
  setSelectedTargetLanguage
} from 'src/features/translateArticle';
import { useTranslateArticle } from 'src/hooks/useTranslateArticle';
import { setKeywordListNewsletter } from 'src/utils/setKeywordListNewsletter';
import Empty from 'src/components/Empty';

// ============= TYPES =============
type MediaType = 'online_media' | 'broadcast' | 'print_media';

// ============= MEDIA ARTICLE CARD (Online News, Broadcast, Print Media) =============
interface MediaArticleCardProps {
  items: INormalizedStream[];
  mediaType: MediaType;
  previewOnly?: boolean;
  isDummy?: boolean;
}

const dummyData: INormalizedStream[] = [
  {
    originalId: 'sm_001',
    source: 'socialMedia',
    fromName: 'Example Inc.',
    displayPicture: '',
    timestamp: new Date('2024-11-04T10:30:00'),
    title: 'Example Title',
    content:
      'Lorem ipsum dolor sit amet consectetur adipisicing elit. Ea praesentium, animi facilis commodi mollitia saepe, quis voluptas laudantium unde maxime in consectetur molestias aut ullam placeat et eum explicabo excepturi?',
    sentiment: 'positive',
    keywords: 'product:2, launch:1, innovation:1, technology:2',
    prValue: '2500000',
    platform: 'instagram',
    link: '#',
    audience: 15420,
    view: 3250,
    like: 234,
    comment: 45,
    engagement: 1.81
  }
];

const MediaArticleCard: React.FC<MediaArticleCardProps> = ({
  items,
  previewOnly = false,
  isDummy = false,
  mediaType: articleMediaType
}) => {
  const dispatch = useAppDispatch();
  // Gunakan useState dengan object untuk menyimpan state see more setiap item
  // const [seeMore, setSeeMore] = React.useState<Record<string, boolean>>({});

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment.toLowerCase()) {
      case 'positive':
        return '#4CAF50';
      case 'negative':
        return '#F44336';
      default:
        return '#1976D2';
    }
  };

  const { selectedNewsLetter, activeSection, translatedStreams, mediaType } =
    useAppSelector((state) => state.storeCustomNewsLetter);

  const { translateResult } = useAppSelector(
    (state) => state.storeTranslateArticle
  );

  const { resTranslateArticle, handleClickTranslateButton } =
    useTranslateArticle();

  const [selectedStreamTranslate, setSelectedStreamTranslate] =
    useState<INormalizedStreamWithLanguage>(null);

  const { selectDataSource, customizeTemplate } = selectedNewsLetter;

  const {
    selectedStreamSosmedList,
    selectedStreamOnlineMediaList,
    selectedStreamBroadcastList,
    selectedStreamPrintMediaList
  } = selectDataSource;

  const setFieldValue = (field: string, value: any) => {
    dispatch(
      reducerUpdateDataSelectDataSource({
        ...selectDataSource,
        [field]: value
      })
    );
  };

  const handleChangeSelectedStreams = (stream: INormalizedStream) => {
    const sectionStreams: INormalizedStream[] = {
      social_media: selectedStreamSosmedList,
      online_media: selectedStreamOnlineMediaList,
      broadcast: selectedStreamBroadcastList,
      print_media: selectedStreamPrintMediaList
    }[activeSection];

    const updatedSelection = sectionStreams.filter(
      (selected) => selected.originalId !== stream.originalId
    );

    let nameState = '';
    switch (activeSection) {
      case 'online_media':
        nameState = 'selectedStreamOnlineMediaList';
        break;
      case 'broadcast':
        nameState = 'selectedStreamBroadcastList';
        break;
      case 'print_media':
        nameState = 'selectedStreamPrintMediaList';
        break;
      default:
        break;
    }

    setFieldValue(nameState, updatedSelection);
  };

  const isLoadingTranslate = (item: INormalizedStream) => {
    return (
      resTranslateArticle.loading &&
      selectedStreamTranslate &&
      item.originalId === selectedStreamTranslate.originalId
    );
  };

  const isContentTranslated = (item: INormalizedStream) => {
    return Boolean(
      translatedStreams.find((value) => value.originalId === item.originalId)
    );
  };

  const renderTitle = (item: INormalizedStream) => {
    const getTranslatedStream = translatedStreams.find(
      (value) => value.originalId === item.originalId
    );
    if (getTranslatedStream) {
      return getTranslatedStream.title;
    }
    return item.title;
  };

  const renderContent = (item: INormalizedStream) => {
    const getTranslatedStream = translatedStreams.find(
      (value) => value.originalId === item.originalId
    );
    if (getTranslatedStream) {
      return getTranslatedStream.content.length < 500
        ? getTranslatedStream.content
        : getTranslatedStream.content.slice(0, 500) + '...';
    }
    return item.content.length < 500
      ? item.content
      : item.content.slice(0, 500) + '...';
  };

  const getAdditionalText = (item: INormalizedStreamWithLanguage) => {
    const getTranslatedStream = translatedStreams.find(
      (value) => value.originalId === item.originalId
    );
    if (getTranslatedStream) return 'See Original';
    return '';
  };

  const handleSeeOriginal = (item: INormalizedStream) => {
    dispatch(reducerRemoveTranslatedStreams(item));
  };

  useEffect(() => {
    if (
      !translateResult ||
      !selectedStreamTranslate ||
      mediaType !== articleMediaType
    )
      return;
    dispatch(
      reducerUpdateTranslatedStreams({
        ...selectedStreamTranslate,
        title: translateResult.translation_translateArticle.title,
        content: translateResult.translation_translateArticle.content
      })
    );
  }, [translateResult, selectedStreamTranslate, mediaType]);

  useEffect(() => {
    return () => {
      dispatch(resetTranslation());
    };
  }, []);

  // const handleToggleSeeMore = (itemTitle: string) => {
  //   setSeeMore((prev) => ({
  //     ...prev,
  //     [itemTitle]: !prev[itemTitle]
  //   }));
  // };

  return (
    <Stack spacing={3} width={'100%'}>
      {(isDummy ? dummyData : items).map((item, index) => (
        <Box
          key={index}
          sx={{
            background: '#fff',
            borderRadius: 1,
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            position: 'relative'
          }}
        >
          {/* Delete Button */}
          {items.length > 0 && !previewOnly && (
            <IconButton
              onClick={() => handleChangeSelectedStreams(item)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: '#999',
                '&:hover': {
                  color: '#F44336',
                  backgroundColor: 'rgba(244, 67, 54, 0.08)'
                }
              }}
              size="small"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}

          {/* Card Header */}
          <Box sx={{ p: 2.5 }}>
            <Stack direction="row" justifyContent="end" sx={{ py: 1 }}>
              <TranslateButton
                key={index}
                handleSeeOriginal={() => handleSeeOriginal(item)}
                handleTranslate={(selectedLanguage: string) => {
                  setSelectedStreamTranslate({
                    ...item,
                    language: selectedLanguage
                  });
                  dispatch(setSelectedTargetLanguage(selectedLanguage));
                  dispatch(reducerUpdateMediaType(articleMediaType));
                  handleClickTranslateButton({
                    title: item.title,
                    content: item.content,
                    language: selectedLanguage ? selectedLanguage : undefined
                  });
                }}
                loading={
                  selectedStreamTranslate &&
                  item.originalId === selectedStreamTranslate.originalId
                    ? resTranslateArticle.loading
                    : false
                }
                isTranslated={isContentTranslated(item)}
                mode="dropdown"
                additionalText={getAdditionalText(item)}
              />
            </Stack>
            {isLoadingTranslate(item) ? null : (
              <>
                {item.title && (
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#1976D2',
                      fontWeight: 600,
                      mb: 1,
                      maxWidth: '90%'
                    }}
                  >
                    {renderTitle(item)}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: '#999', mb: 1.5 }}>
                  {moment(item.timestamp).format('ddd, DD MMM YYYY HH:mm')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Badge
                    overlap="circular"
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right'
                    }}
                  >
                    <Avatar
                      alt={item.title}
                      src={item.displayPicture}
                      sx={{ width: 40, height: 40 }}
                    />
                  </Badge>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    {item.fromName}
                  </Typography>
                </Stack>
              </>
            )}
          </Box>

          {/* Divider */}
          <Box sx={{ height: 1, background: '#E0E0E0' }} />

          {/* Card Body */}
          <Box sx={{ p: 2.5 }}>
            {/* Content Text */}
            {isLoadingTranslate(item) ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <CircularProgress disableShrink size={64} />
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: '#666', lineHeight: 1.6 }}
                dangerouslySetInnerHTML={{
                  // __html: seeMore[item.title]
                  //   ? item.content
                  //   : item.content.slice(0, 500) + '...'
                  __html: renderContent(item)
                }}
              />
            )}

            {/* Link Source */}
            {item.link && item.link !== '#' && (
              <Box sx={{ pb: 2.5, pt: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 400,
                    color: '#0781B6FF',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    cursor: 'pointer',
                    '&:hover': {
                      fontWeight: 600
                    }
                  }}
                  onClick={() => {
                    if (item.link) {
                      window.open(item.link, '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                  {customizeTemplate.readMoreLabel || 'Link to source'}
                </Typography>
              </Box>
            )}

            {/* Keywords Section */}
            {customizeTemplate.isNumberOfKeywords &&
              item.keywords &&
              item.keywords.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ fontWeight: 600, mr: 1, color: '#333' }}
                  >
                    Keywords:
                  </Typography>
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ color: '#666' }}
                  >
                    {setKeywordListNewsletter(
                      item.content,
                      item.keywords.split(',').map((item) => item.trim())
                    ).join(', ')}
                  </Typography>
                </Box>
              )}

            {/* PR Value and Sentiment Row */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" sx={{ color: '#666' }}>
                {customizeTemplate.isPrValue ? `PR Value: ${item.prValue}` : ''}
              </Typography>

              {customizeTemplate.isSentiment && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: getSentimentColor(item.sentiment)
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    {item.sentiment}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

// ============= SOCIAL MEDIA CARD =============
interface SocialMediaCardProps {
  items: INormalizedStream[];
  previewOnly?: boolean;
  isDummy?: boolean;
}

interface INormalizedStreamWithLanguage extends INormalizedStream {
  language?: string;
}

const SocialMediaCard: React.FC<SocialMediaCardProps> = ({
  items,
  previewOnly = false,
  isDummy = false
}) => {
  const dispatch = useAppDispatch();
  const getSentimentColor = (sentiment: string) => {
    switch (sentiment.toLowerCase()) {
      case 'positive':
        return '#4CAF50';
      case 'negative':
        return '#F44336';
      default:
        return '#1976D2';
    }
  };

  const {
    isLikeMetric,
    isCommentMetric,
    isViewMetric,
    isEngagementMetric,
    isSentimentMetric
  } = useAppSelector(
    (state) => state.storeCustomNewsLetter.selectedNewsLetter.customizeTemplate
  );

  const { selectedNewsLetter, translatedStreams, mediaType } = useAppSelector(
    (state) => state.storeCustomNewsLetter
  );

  const { translateResult } = useAppSelector(
    (state) => state.storeTranslateArticle
  );

  const { resTranslateArticle, handleClickTranslateButton } =
    useTranslateArticle();

  const [selectedStreamTranslate, setSelectedStreamTranslate] =
    useState<INormalizedStreamWithLanguage>(null);

  const { selectDataSource, customizeTemplate } = selectedNewsLetter;
  const { selectedStreamSosmedList } = selectDataSource;

  const setFieldValue = (field: string, value: any) => {
    dispatch(
      reducerUpdateDataSelectDataSource({
        ...selectDataSource,
        [field]: value
      })
    );
  };

  const handleChangeSelectedStreams = (stream: INormalizedStream) => {
    const sectionStreams: INormalizedStream[] = selectedStreamSosmedList;
    const updatedSelection = sectionStreams.filter(
      (selected) => selected.originalId !== stream.originalId
    );

    setFieldValue('selectedStreamSosmedList', updatedSelection);
  };

  const isLoadingTranslate = (item: INormalizedStream) => {
    return (
      resTranslateArticle.loading &&
      selectedStreamTranslate &&
      item.originalId === selectedStreamTranslate.originalId
    );
  };

  const isContentTranslated = (item: INormalizedStream) => {
    return Boolean(
      translatedStreams.find((value) => value.originalId === item.originalId)
    );
  };

  const renderContent = (item: INormalizedStream) => {
    const getTranslatedStream = translatedStreams.find(
      (value) => value.originalId === item.originalId
    );
    if (getTranslatedStream) {
      return getTranslatedStream.content;
    }
    return item.content;
  };

  const getAdditionalText = (item: INormalizedStreamWithLanguage) => {
    const getTranslatedStream = translatedStreams.find(
      (value) => value.originalId === item.originalId
    );
    if (getTranslatedStream) return 'See Original';
    return '';
  };

  const handleSeeOriginal = (item: INormalizedStream) => {
    dispatch(reducerRemoveTranslatedStreams(item));
  };

  useEffect(() => {
    if (
      !translateResult ||
      !selectedStreamTranslate ||
      mediaType !== 'social_media'
    )
      return;
    dispatch(
      reducerUpdateTranslatedStreams({
        ...selectedStreamTranslate,
        content: translateResult.translation_translateArticle.content
      })
    );
  }, [translateResult, selectedStreamTranslate, mediaType]);

  useEffect(() => {
    return () => {
      dispatch(resetTranslation());
    };
  }, []);

  return (
    <Stack spacing={3} width={'100%'} maxWidth={'100%'}>
      {(isDummy ? dummyData : items).map((item, index) => (
        <Box
          key={index}
          sx={{
            background: '#fff',
            borderRadius: 1,
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            width: '100%',
            maxWidth: '100%',
            position: 'relative'
          }}
        >
          {/* Delete Button */}
          {items.length > 0 && !previewOnly && (
            <IconButton
              onClick={() => handleChangeSelectedStreams(item)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: '#999',
                '&:hover': {
                  color: '#F44336',
                  backgroundColor: 'rgba(244, 67, 54, 0.08)'
                }
              }}
              size="small"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}

          {/* Card Header */}
          <Box sx={{ p: 2.5 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                <Badge
                  overlap="circular"
                  badgeContent={
                    <SocialMediaIcon
                      socialMedia={item.platform as TSocialMedia}
                      sx={{
                        width: 16,
                        height: 16,
                        color: getSocialMediaColor(
                          item.platform as TSocialMedia
                        )
                      }}
                    />
                  }
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                  }}
                >
                  <Avatar
                    alt={item.fromName}
                    src={item.displayPicture}
                    sx={{ width: 40, height: 40 }}
                  />
                </Badge>
                <Typography
                  variant="h6"
                  sx={{ color: '#1976D2', fontWeight: 600 }}
                >
                  {item.fromName}
                </Typography>
              </Stack>
              <TranslateButton
                key={index}
                handleSeeOriginal={() => handleSeeOriginal(item)}
                handleTranslate={(selectedLanguage: string) => {
                  setSelectedStreamTranslate({
                    ...item,
                    language: selectedLanguage
                  });
                  dispatch(setSelectedTargetLanguage(selectedLanguage));
                  dispatch(reducerUpdateMediaType('social_media'));
                  handleClickTranslateButton({
                    title: '',
                    content: item.content,
                    language: selectedLanguage ? selectedLanguage : undefined
                  });
                }}
                loading={
                  selectedStreamTranslate &&
                  item.originalId === selectedStreamTranslate.originalId
                    ? resTranslateArticle.loading
                    : false
                }
                isTranslated={isContentTranslated(item)}
                mode="dropdown"
                additionalText={getAdditionalText(item)}
              />
            </Stack>
            <Typography variant="body2" sx={{ color: '#999' }}>
              {moment(item.timestamp).format('ddd, DD MMM YYYY HH:mm')}
            </Typography>
          </Box>

          {/* Divider */}
          <Box sx={{ height: 1, background: '#E0E0E0' }} />

          {/* Card Body */}
          <Box sx={{ p: 2.5 }}>
            {/* Content Text */}
            {isLoadingTranslate(item) ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <CircularProgress disableShrink size={64} />
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: '#666', lineHeight: 1.6, mb: 2 }}
                dangerouslySetInnerHTML={{
                  __html: renderContent(item)
                }}
              />
            )}

            {/* Social Media Metrics */}
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              <>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Followers {item.audience}
                </Typography>
              </>

              {isViewMetric && (
                <>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    |
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    View {0}
                  </Typography>
                </>
              )}

              {isLikeMetric && (
                <>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    |
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Like {item.like}
                  </Typography>
                </>
              )}
              {isCommentMetric && (
                <>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    |
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Comment {item.comment}
                  </Typography>
                </>
              )}

              {isEngagementMetric && (
                <>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    |
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Engagement {item.engagement}
                  </Typography>
                </>
              )}
            </Stack>

            {/* Sentiment */}
            {isSentimentMetric && (
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      background: getSentimentColor(item.sentiment)
                    }}
                  />
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    {item.sentiment}
                  </Typography>
                </Stack>
              </Stack>
            )}

            {/* Link Source */}
            {item.link && item.link !== '#' && (
              <Box sx={{ pb: 2.5, pt: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 400,
                    color: '#0781B6FF',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    cursor: 'pointer',
                    '&:hover': {
                      fontWeight: 600
                    }
                  }}
                  onClick={() => {
                    if (item.link) {
                      window.open(item.link, '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                  {customizeTemplate.readMoreLabel || 'Link to source'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

// ============= MAIN MEDIA SECTION WRAPPER =============
interface ICallbackOnClick {
  souceType: 'social_media' | 'online_media' | 'offline_media';
  section: MediaType | 'social_media';
}
interface MediaSectionProps {
  label: string;
  mainColor: string;
  fontColor: string;
  mediaType: MediaType | 'social_media';
  items: INormalizedStream[];
  onClick?: (e: ICallbackOnClick) => void;
  previewOnly?: boolean;
  isDummy?: boolean;
  // ref?: React.RefObject<HTMLDivElement>;
}

const scrollToSection = (targetRef: React.RefObject<HTMLDivElement>) => {
  const element = targetRef.current;
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const MediaSection = React.forwardRef<HTMLDivElement, MediaSectionProps>(
  (
    {
      mainColor = '#0B85AE',
      fontColor = '#FEFEFEFF',
      label,
      mediaType,
      items = [],
      onClick,
      previewOnly = false,
      isDummy = false
    },
    ref // ref sekarang sebagai parameter kedua, bukan dari props
  ) => {
    const dispatch = useAppDispatch();
    const [showMore, setShowMore] = React.useState(false);
    const isSocialMedia = mediaType === 'social_media';

    const { activeSection } = useAppSelector(
      (state) => state.storeCustomNewsLetter
    );

    React.useEffect(() => {
      // Hanya scroll jika section ini adalah active section
      if (
        !showMore &&
        ref &&
        typeof ref !== 'function' &&
        ref.current &&
        activeSection === mediaType
      ) {
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, [showMore, ref, activeSection, mediaType]);

    const handleClick = () => {
      if (previewOnly) return;
      if (onClick) {
        switch (mediaType) {
          case 'broadcast':
            onClick({ souceType: 'offline_media', section: 'broadcast' });
            break;
          case 'print_media':
            onClick({ souceType: 'offline_media', section: 'print_media' });
            break;
          case 'online_media':
            onClick({ souceType: 'online_media', section: 'online_media' });
            break;
          case 'social_media':
            onClick({ souceType: 'social_media', section: 'social_media' });
            break;
          default:
            break;
        }
      }
    };

    const handleShowMore = () => {
      setShowMore(!showMore);
      dispatch(reducerUpdateActiveSection(mediaType));
    };

    const listStream = React.useMemo(() => {
      return showMore ? items : items.slice(0, 3);
    }, [showMore, items]);

    return (
      <div
        id={`section-${mediaType}`}
        style={{
          background: '#ffffff'
        }}
        ref={ref}
        onClick={handleClick}
      >
        <Box
          sx={{
            background: mainColor,
            minHeight: 60,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            px: 2
          }}
        >
          <Typography
            variant="h5"
            textAlign="center"
            color={fontColor}
            fontWeight={700}
          >
            {label}
          </Typography>
        </Box>

        <Box
          sx={{
            minHeight: 300,
            p: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',

            ...(!previewOnly &&
              (activeSection === mediaType
                ? {
                    border: '2px solid #009FE3'
                  }
                : {
                    '&:hover': {
                      cursor: 'pointer',
                      border: '2px solid #009FE3'
                    }
                  }))
          }}
        >
          {items.length === 0 && !isDummy && !previewOnly ? (
            <Stack>
              <Typography variant="body1" textAlign="center" fontWeight={500}>
                <PlaylistAddCircleIcon
                  sx={{ width: 50, height: 50, color: 'gray' }}
                />
              </Typography>
              <Typography variant="caption" fontWeight={500}>
                Click here to start adding articles or social media posts.
              </Typography>
            </Stack>
          ) : items.length === 0 && previewOnly ? (
            <Stack>
              <Typography variant="body1" textAlign="center" fontWeight={500}>
                <Empty message="No data available" />
              </Typography>
            </Stack>
          ) : (
            <Box sx={{ width: '100%' }}>
              {isSocialMedia ? (
                <SocialMediaCard
                  items={listStream}
                  previewOnly={previewOnly}
                  isDummy={items.length === 0}
                />
              ) : (
                <MediaArticleCard
                  items={listStream}
                  mediaType={mediaType as MediaType}
                  previewOnly={previewOnly}
                  isDummy={items.length === 0}
                />
              )}
              {items.length > 3 && (
                <Stack direction="row" justifyContent="center" sx={{ p: 2.5 }}>
                  <Button
                    onClick={handleShowMore}
                    size="small"
                    variant="contained"
                    sx={{
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {showMore ? 'Show Less' : 'Show More'}
                  </Button>
                </Stack>
              )}
            </Box>
          )}
        </Box>
      </div>
    );
  }
);

// Tambahkan display name untuk debugging
MediaSection.displayName = 'MediaSection';

export default MediaSection;
export { MediaArticleCard, SocialMediaCard, scrollToSection };
export type { MediaType, ICallbackOnClick };
